import { useCallback, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'

import { captureMessage, SENTRY_SEVERITY } from 'src/utils/sentry'
import { sanitizeSentryPage } from 'src/utils/sentry/sanitizeSentryUrl'

/**
 * 지연 추적용 breadcrumb 기록 함수
 * - feature: (예: 'self_reg_upload', 'map_locate')
 * - step: 현재 단계 (예: 'start', 'gps_fetching', 'complete', 'fail', 'timeout')
 */
export function addDelayTrackingBreadcrumb(
  feature: string,
  step: string,
  data?: Record<string, unknown>,
) {
  const isWarningLevel = step === 'timeout' || step === 'fail'

  Sentry.addBreadcrumb({
    category: `Delay [${feature}]`,
    message:
      step === 'timeout' && data?.lastStep ? `[timeout] ${String(data.lastStep)}` : `${step}`,
    level: isWarningLevel ? SENTRY_SEVERITY.Warning : SENTRY_SEVERITY.Info,
    data: {
      page: sanitizeSentryPage(),
      step,
      ...data,
    },
  })
}

interface UseDelayTrackingOptions {
  feature: string
  timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 30_000

export function useDelayTrackingMonitor({
  feature,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseDelayTrackingOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startedAtRef = useRef<number | null>(null)
  const lastStepRef = useRef<string | null>(null)
  const contextRef = useRef<Record<string, unknown>>({})

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = null
    startedAtRef.current = null
    lastStepRef.current = null
    contextRef.current = {}
  }, [])

  /**
   * 작업 시작 + 타이머 세팅
   * @param context start 시점에 같이 남길 부가 정보
   * @param overrideTimeoutMs 이번 호출에 한해 기본 timeoutMs를 override
   */
  const start = useCallback(
    (context?: Record<string, unknown>, overrideTimeoutMs?: number) => {
      const effectiveTimeoutMs = overrideTimeoutMs ?? timeoutMs

      clear()

      startedAtRef.current = Date.now()
      contextRef.current = context ?? {}
      lastStepRef.current = 'start'

      addDelayTrackingBreadcrumb(feature, 'start', {
        elapsedMs: 0,
        ...context,
      })

      timerRef.current = setTimeout(() => {
        const elapsedMs = Date.now() - (startedAtRef.current ?? Date.now()) // 시작 이후 경과 시간

        addDelayTrackingBreadcrumb(feature, 'timeout', {
          elapsedMs,
          lastStep: lastStepRef.current,
          ...contextRef.current,
        })

        captureMessage(`[${feature}] ${effectiveTimeoutMs / 1000}초 초과`, {
          level: SENTRY_SEVERITY.Info,
          tags: {
            feature,
            lastStep: lastStepRef.current ?? 'unknown',
          },
          extra: {
            timeoutMs: effectiveTimeoutMs,
            elapsedMs,
            ...contextRef.current,
          },
        })
      }, effectiveTimeoutMs)
    },
    [clear, feature, timeoutMs],
  )

  /**
   * 중간 단계 기록
   *
   * - 현재 진행 단계를 breadcrumb로 기록
   * - 전달된 data는 timeout 발생 시 함께 확인할 수 있도록 내부 context에 누적
   *
   * @param stepName 현재 진행 단계
   * @param data 단계별 부가 정보
   */
  const step = useCallback(
    (stepName: string, data?: Record<string, unknown>) => {
      if (!startedAtRef.current) return

      const elapsedMs = Date.now() - startedAtRef.current

      lastStepRef.current = stepName

      // timeout 시에도 볼 수 있도록 누적
      if (data) {
        contextRef.current = {
          ...contextRef.current,
          ...data,
        }
      }

      addDelayTrackingBreadcrumb(feature, stepName, {
        elapsedMs,
        ...data,
      })
    },
    [feature],
  )

  /**
   * 작업 종료 (성공/실패)
   *
   * - 최종 breadcrumb 기록
   * - 전체 소요 시간(elapsedMs) 함께 기록
   * - 타이머 및 내부 상태 정리
   *
   * @param outcome 작업 결과 ('complete' | 'fail')
   * @param extra 종료 시 함께 기록할 부가 정보
   */
  const finish = useCallback(
    (outcome: 'complete' | 'fail', extra?: Record<string, unknown>) => {
      if (!startedAtRef.current) return

      const elapsedMs = Date.now() - startedAtRef.current

      addDelayTrackingBreadcrumb(feature, outcome, {
        elapsedMs,
        ...extra,
      })

      clear()
    },
    [feature, clear],
  )

  useEffect(() => {
    return () => clear()
  }, [clear])

  return {
    start,
    step,
    finish,
    clear,
  }
}
